import React, { useContext, useEffect, useState } from 'react';
import { LuGraduationCap } from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import BackNav from '../../components/navigation/BackNav';
import LearnPageShell from './LearnPageShell';
import { setDailyReviewGoal } from '../../utils/learningPreferences';
import './learn.css';

export default function LearningSettingsPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [dailyGoal, setDailyGoal] = useState('10');
  const [learningGoal, setLearningGoal] = useState('');
  const [difficulty, setDifficulty] = useState('INTERMEDIATE');
  const [status, setStatus] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [tax, prefs] = await Promise.all([
          apiClient.get('/api/v1/modules'),
          apiClient.get('/api/v1/learning/preferences'),
        ]);
        setModules(Array.isArray(tax.data) ? tax.data : []);
        const ids = prefs.data?.selectedModuleIds || [];
        setSelected(new Set(ids));
        setDailyGoal(String(prefs.data?.dailyGoal || 10));
        setLearningGoal(prefs.data?.learningGoal || prefs.data?.learningFocus || '');
        setDifficulty(prefs.data?.preferredDifficulty || 'INTERMEDIATE');
      } catch {
        setStatus('Could not load preferences.');
      }
    })();
  }, [apiClient]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setStatus('');
    try {
      await apiClient.put('/api/v1/learning/preferences', {
        learningGoal: learningGoal.trim(),
        learningFocus: learningGoal.trim(),
        dailyGoal: Number(dailyGoal) || 10,
        preferredDifficulty: difficulty,
        selectedModuleIds: [...selected],
        onboardingCompleted: true,
      });
      setDailyReviewGoal(Number(dailyGoal) || 10);
      setStatus('Preferences saved.');
    } catch {
      setStatus('Save failed.');
    }
  };

  return (
    <>
      <PageMeta title="Learning interests" description="Manage what you want to learn on QnaRead" />
      <LearnPageShell userProfile={userProfile}>
        <BackNav to="/settings" label="Settings" />
        <section className="sh-block learn-settings-block">
          <header className="sh-block-head">
            <div className="sh-block-head-main">
              <span className="sh-block-icon" aria-hidden><LuGraduationCap /></span>
              <div>
                <h1 className="sh-block-title">Learning interests</h1>
                <p className="sh-block-sub">
                  Used for Study hub and Learn recommendations. To read catalog questions, add notebooks on Learn →
                  Library notebooks. Custom Learn notebooks stay private to you.
                </p>
              </div>
            </div>
          </header>
          <div className="learn-form-panel learn-form-fields">
            <div className="learn-form-field">
              <span className="learn-form-label">Modules</span>
              <div className="onboarding-module-grid learn-module-grid--settings">
                {modules.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`onboarding-module-card${selected.has(m.id) ? ' selected' : ''}`}
                    onClick={() => toggle(m.id)}
                  >
                    {m.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="learn-form-field">
              <label htmlFor="lg">Primary goal</label>
              <input id="lg" className="learn-input" value={learningGoal} onChange={(e) => setLearningGoal(e.target.value)} />
            </div>
            <div className="learn-form-row">
              <div className="learn-form-field">
                <label htmlFor="dg">Daily review target</label>
                <input id="dg" type="number" min={1} max={200} className="learn-input" value={dailyGoal} onChange={(e) => setDailyGoal(e.target.value)} />
              </div>
              <div className="learn-form-field">
                <label htmlFor="df">Difficulty</label>
                <select id="df" className="learn-filter-select" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                  <option value="BEGINNER">Beginner</option>
                  <option value="INTERMEDIATE">Intermediate</option>
                  <option value="ADVANCED">Advanced</option>
                </select>
              </div>
            </div>
            <div className="learn-form-actions learn-create-actions">
              <button type="button" className="home-btn home-btn-primary" onClick={save}>
                Save changes
              </button>
            </div>
            {status && <p className="learn-status-msg" role="status">{status}</p>}
          </div>
        </section>
      </LearnPageShell>
    </>
  );
}
